import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Sparkles, X, Send, Mic, ThumbsUp, ThumbsDown, Copy, Plus, Clock, HelpCircle, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import VoiceInput from "./VoiceInput";

/**
 * AlfredPanel — persistent right-side AI assistant (Procore-Assist style).
 * Slide-in rail (not modal), Ctrl+K to toggle, context-aware recommendations,
 * chat with sources + thumbs feedback, "New / History" tabs.
 */
export default function AlfredPanel({ open, onOpenChange }) {
  const [tab, setTab] = useState("new"); // new | history | learn
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [history, setHistory] = useState([]); // saved sessions in localStorage
  const [feedback, setFeedback] = useState({}); // messageIndex -> 'up'|'down'
  const location = useLocation();
  const nav = useNavigate();
  const scrollRef = useRef(null);

  const ctx = useContextRecommendations();

  const persistCurrentSession = useCallback(() => {
    if (messages.length === 0) return;
    const first = messages.find(m => m.role === "user")?.text?.slice(0, 60) || "Session";
    const session = { id: Date.now(), title: first, saved_at: new Date().toISOString(), messages };
    const all = JSON.parse(localStorage.getItem("alfred_sessions") || "[]");
    const updated = [session, ...all].slice(0, 20);
    localStorage.setItem("alfred_sessions", JSON.stringify(updated));
    setHistory(updated);
  }, [messages]);

  useEffect(() => {
    setHistory(JSON.parse(localStorage.getItem("alfred_sessions") || "[]"));
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const submit = async (utterance) => {
    const q = (utterance ?? text).trim();
    if (!q) return;
    setBusy(true);
    setMessages(m => [...m, { role: "user", text: q }]);
    setText("");
    try {
      const { data } = await api.post("/voice/command", {
        text: q,
        current_path: location.pathname,
        context: ctx.machineContext,
      });
      setMessages(m => [...m, {
        role: "alfred",
        text: data.say || "OK",
        intent: data.intent,
        sources: (data.sources || ctx.sources),
        action: data,
      }]);
      if (data?.executed && data?.path) {
        const qp = data.params?.q ? `?q=${encodeURIComponent(data.params.q)}` : "";
        // Do not auto-close — panel is persistent per Procore-style. Just navigate.
        setTimeout(() => nav(data.path + qp), 400);
      }
    } catch (e) {
      setMessages(m => [...m, { role: "alfred", text: "Sorry — I couldn't reach the command service." }]);
    } finally { setBusy(false); }
  };

  const startNewSession = () => {
    persistCurrentSession();
    setMessages([]);
    setText("");
    setTab("new");
  };

  const openSession = (s) => { setMessages(s.messages || []); setTab("new"); };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  // Ctrl+J to toggle (Ctrl+K is reserved for the command palette)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "j") {
        e.preventDefault(); onOpenChange(!open);
      }
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  return (
    <>
      {/* Backdrop is intentionally OMITTED — persistent side panel does not block the main workspace */}
      <aside
        className={`fixed top-0 right-0 h-screen w-[420px] max-w-full bg-white border-l shadow-2xl z-50 transition-transform duration-300 ease-out flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="alfred-panel"
        aria-hidden={!open}
      >
        {/* Header */}
        <header className="p-4 border-b flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-pink grid place-items-center"><Sparkles className="w-5 h-5 text-white" /></div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="font-bold">Alfred</div>
              <Badge className="bg-neutral-100 text-neutral-600 border-0 text-[10px]">Beta</Badge>
            </div>
            <div className="text-[11px] text-neutral-500 truncate">Your AI teammate · <span className="font-mono">Ctrl+J</span> to toggle</div>
          </div>
          <button onClick={() => onOpenChange(false)} className="text-neutral-400 hover:text-neutral-900" aria-label="Close" data-testid="alfred-close-btn">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Tabs */}
        <div className="px-4 pt-3 flex items-center gap-2 border-b">
          {[
            { k: "new", label: "New", icon: Plus },
            { k: "history", label: "History", icon: Clock },
            { k: "learn", label: "Learn More", icon: HelpCircle },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`alfred-tab-${t.k}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg -mb-px border-b-2 transition-colors ${tab === t.k ? "border-pink text-pink font-semibold" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
          <button onClick={startNewSession} className="ml-auto text-xs text-neutral-500 hover:text-pink" data-testid="alfred-new-session-btn">+ New chat</button>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {tab === "new" && (
            <div className="p-4 space-y-4">
              {/* Context-aware Recommendation card */}
              {ctx.recommendations.length > 0 && messages.length === 0 && (
                <section className="rounded-xl border p-4 bg-gradient-to-br from-pink-50 to-white" data-testid="alfred-recommendations">
                  <div className="text-sm font-semibold mb-1 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink" /> Recommendation
                  </div>
                  <div className="text-xs text-neutral-600 mb-3">{ctx.recommendationLead}</div>
                  <ul className="space-y-2.5 text-sm">
                    {ctx.recommendations.map((r, i) => (
                      <li key={i} className="flex gap-2" data-testid={`alfred-rec-${i}`}>
                        <span className="font-bold text-neutral-900 shrink-0">({String.fromCharCode(65 + i)})</span>
                        <div className="min-w-0">
                          <button
                            onClick={() => submit(r.utterance || r.title)}
                            className="text-left"
                          >
                            <span className="font-semibold text-neutral-900 hover:text-pink">{r.title}</span>
                            <span className="text-neutral-600">: {r.description}</span>
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {ctx.sources && ctx.sources.length > 0 && (
                    <div className="mt-4 pt-3 border-t">
                      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5 flex items-center gap-1">
                        Sources <HelpCircle className="w-3 h-3" />
                      </div>
                      <ol className="text-xs text-pink space-y-0.5 list-decimal ml-4">
                        {ctx.sources.map((s, i) => (<li key={i}><span className="hover:underline cursor-pointer">{s}</span></li>))}
                      </ol>
                    </div>
                  )}
                </section>
              )}

              {/* Empty state */}
              {messages.length === 0 && ctx.recommendations.length === 0 && (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-pink/10 grid place-items-center mx-auto mb-3">
                    <Sparkles className="w-7 h-7 text-pink" />
                  </div>
                  <div className="font-semibold">How can I help?</div>
                  <div className="text-sm text-neutral-500 mt-1">Ask a question or tap the mic to speak.</div>
                </div>
              )}

              {/* Chat */}
              {messages.length > 0 && (
                <div className="space-y-3">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`alfred-msg-${i}`}>
                      <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-navy text-white" : "bg-neutral-100 text-neutral-800"}`}
                           style={m.role === "user" ? { backgroundColor: "#00214D" } : {}}>
                        <div className="text-[10px] uppercase tracking-widest opacity-60 mb-0.5">{m.role === "user" ? "You" : "Alfred"}</div>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                        {m.intent && (
                          <div className="mt-2"><Badge variant="outline" className="text-[10px]">{m.intent}</Badge></div>
                        )}
                        {m.role === "alfred" && (
                          <div className="mt-2 flex items-center gap-2 text-neutral-400">
                            <button onClick={() => setFeedback(f => ({...f, [i]: "up"}))} data-testid={`alfred-fb-up-${i}`}
                              className={`hover:text-teal ${feedback[i] === "up" ? "text-teal" : ""}`}><ThumbsUp className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setFeedback(f => ({...f, [i]: "down"}))} data-testid={`alfred-fb-down-${i}`}
                              className={`hover:text-red-500 ${feedback[i] === "down" ? "text-red-500" : ""}`}><ThumbsDown className="w-3.5 h-3.5" /></button>
                            <button onClick={() => copy(m.text)} className="hover:text-neutral-900" data-testid={`alfred-copy-${i}`}><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {busy && (
                    <div className="flex justify-start"><div className="bg-neutral-100 rounded-2xl px-4 py-2 text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Alfred is thinking…</div></div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "history" && (
            <div className="p-4">
              {history.length === 0 ? (
                <div className="text-center text-neutral-500 py-10 text-sm">No past sessions yet.</div>
              ) : (
                <ul className="space-y-2">
                  {history.map(s => (
                    <li key={s.id}>
                      <button onClick={() => openSession(s)} className="w-full text-left rounded-lg border p-3 hover:border-pink transition-colors"
                        data-testid={`alfred-history-${s.id}`}>
                        <div className="font-medium text-sm truncate">{s.title}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">{new Date(s.saved_at).toLocaleString()}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "learn" && (
            <div className="p-4 space-y-3 text-sm text-neutral-700">
              <p className="font-semibold">What can I ask Alfred?</p>
              <ul className="list-disc ml-5 space-y-1.5 text-neutral-600">
                <li>"Take me to compliance" / "Open my documents"</li>
                <li>"Create a job called Senior Product Designer"</li>
                <li>"Search for React engineers with startup experience"</li>
                <li>"Move Alex to Offered" (on the pipeline)</li>
                <li>"Export evidence pack" (HR Admin+)</li>
                <li>"Generate a job description for Staff SRE"</li>
              </ul>
              <p className="text-xs text-neutral-500 pt-2">Alfred respects your role. Actions outside your permissions will be politely refused.</p>
            </div>
          )}
        </div>

        {/* Input row */}
        <div className="p-4 border-t bg-white">
          <div className="flex items-center gap-2 border-2 border-neutral-200 rounded-2xl px-3 py-2 focus-within:border-pink transition-colors">
            <input
              className="flex-1 outline-none text-base bg-transparent placeholder:text-neutral-400"
              placeholder="Ask Alfred anything…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              disabled={busy}
              data-testid="alfred-input"
              autoFocus={open}
            />
            <VoiceInput onTranscript={(t) => submit(t)} testId="alfred-mic" />
            <Button onClick={() => submit()} disabled={busy || !text.trim()} className="btn-primary rounded-full h-9 w-9 p-0 grid place-items-center" data-testid="alfred-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="text-[10px] text-neutral-400 mt-2 text-center">AI-generated responses should be verified for accuracy.</div>
        </div>
      </aside>
    </>
  );
}

/** Client-side rules engine for context-aware recommendations. */
function useContextRecommendations() {
  const loc = useLocation();
  const params = useParams();
  const path = loc.pathname;

  return useMemo(() => {
    // Recruit Job Detail
    if (/^\/app\/recruit\/jobs\/[^/]+$/.test(path)) {
      return {
        recommendationLead: "Based on this job, what would you like to do?",
        recommendations: [
          { title: "Publish to job boards", description: "Distribute this role to LinkedIn, Indeed and Seek in one click.",
            utterance: "Publish this job to LinkedIn and Indeed" },
          { title: "Find matching candidates", description: "Run an AI talent match against the current pool.",
            utterance: "Search for candidates matching this role" },
          { title: "Invite a candidate", description: "Send a one-way video interview link.",
            utterance: "Invite a candidate to this job" },
        ],
        sources: ["Current job pipeline", "Candidate pool", "AI talent match model (Claude Sonnet 5)"],
        machineContext: { page: "job_detail", entity_id: params.jobId || null },
      };
    }
    // Candidate detail
    if (/^\/app\/recruit\/candidates\//.test(path)) {
      return {
        recommendationLead: "For this candidate, you can:",
        recommendations: [
          { title: "Move to Offered", description: "Advance to the offer stage if the scorecard is strong.",
            utterance: "Move this candidate to Offered" },
          { title: "Reject with feedback", description: "Send a polite templated rejection email.",
            utterance: "Reject this candidate" },
          { title: "Summarise transcript", description: "Generate a 3-sentence AI summary of the interview.",
            utterance: "Summarise this candidate's interview" },
        ],
        sources: ["Interview transcript (Whisper)", "Scorecard evaluations", "Job requirements"],
        machineContext: { page: "candidate_detail", entity_id: params.candidateId || null },
      };
    }
    // Compliance
    if (path.startsWith("/app/compliance")) {
      return {
        recommendationLead: "For your compliance posture:",
        recommendations: [
          { title: "Export evidence pack", description: "Generate a Vanta-style PDF for auditors.",
            utterance: "Export the evidence pack" },
          { title: "Fix failing monitors", description: "Show me which controls need attention first.",
            utterance: "What controls are failing right now?" },
          { title: "Framework breakdown", description: "Show ISO 27001 / 42001 / 45001 sub-scores.",
            utterance: "Show framework breakdown" },
        ],
        sources: ["Automated monitors", "Signature evidence", "Certification wallet"],
        machineContext: { page: "compliance" },
      };
    }
    // Employee Portal Home
    if (path === "/me" || path === "/me/") {
      return {
        recommendationLead: "For your onboarding:",
        recommendations: [
          { title: "Sign my contract", description: "Complete the e-signature task.",
            utterance: "Take me to my documents" },
          { title: "Add my certifications", description: "Upload a licence and let AI extract details.",
            utterance: "Take me to my compliance" },
          { title: "Request time off", description: "Book annual or sick leave.",
            utterance: "Take me to my leave page" },
        ],
        sources: ["Your onboarding tasks", "Cert wallet", "Leave balances"],
        machineContext: { page: "me_home" },
      };
    }
    // Default
    return {
      recommendationLead: "Some things you can try:",
      recommendations: [
        { title: "Navigate anywhere", description: "'Take me to compliance' or 'open my documents'.", utterance: "Help" },
        { title: "Create a job", description: "'Create a job called Staff Engineer'.", utterance: "Create a job called Staff Engineer" },
        { title: "AI talent search", description: "'Find React engineers with startup experience'.", utterance: "Search for React engineers with startup experience" },
      ],
      sources: null,
      machineContext: { page: "generic" },
    };
  }, [path, params.jobId, params.candidateId]);
}
